<template>
  <div class="claim_overlay">
    <div class="claim_popup" role="dialog" aria-modal="true">
      <div class="claim_header">{{ getResultTitle }}</div>
      <button v-if="!isLoading" type="button" class="claim_close" aria-label="關閉" @click="close">×</button>

      <div class="claim_body">
        <div class="claim_loading" v-if="isLoading">
          <div class="claim_spinner"></div>
          領取中…
        </div>

        <template v-else-if="hasLoadedDetail && getOfferDetail">
          <div class="coupon_hero">
            <span class="coupon_badge">優惠活動</span>
            <h3 class="coupon_title">{{ getOfferDetail.title }}</h3>
          </div>

          <p v-if="getClaimErrorText" class="claim_message">
            {{ getClaimErrorText }}
          </p>

          <div v-if="hasResultDetail" class="coupon_detail_card">
            <template v-if="getOfferDetail.description">
              <h4>優惠說明</h4>
              <p>{{ getOfferDetail.description }}</p>
            </template>
            <h4>使用門檻</h4>
            <p
              v-for="(text, index) in getPublicRuleDetail.thresholdTexts"
              :key="`threshold_${index}`"
            >
              {{ text }}
            </p>
            <h4>優惠內容</h4>
            <p
              v-for="(text, index) in getPublicRuleDetail.benefitTexts"
              :key="`benefit_${index}`"
            >
              {{ text }}
            </p>
            <h4>領取期間</h4>
            <p>{{ getClaimPeriodText }}</p>
            <template v-if="getValidityText">
              <h4>使用期限</h4>
              <p>{{ getValidityText }}</p>
            </template>
            <h4>使用方式</h4>
            <p>領取後至「已領取」出示優惠券，交由門市人員掃碼確認。</p>
          </div>

        </template>

        <p class="claim_message claim_invalid" v-else>{{ getLoadError }}</p>
      </div>

      <p class="claim_hint" v-if="!isLoading && getResultHint">{{ getResultHint }}</p>

      <div class="claim_footer" v-if="!isLoading">
        <button v-if="getResultStatus === 'birthdayRequired'" type="button" class="button_retry" @click="toBirthdayForm">前往填寫生日</button>
        <template v-else>
          <button v-if="canRetry" type="button" class="button_retry" @click="claim">重新領取</button>
          <button v-if="getLineOfficialAddress" type="button" class="button_line" @click="toLineOfficialAccount">店家LINE@</button>
          <button type="button" class="button_coupons" @click="toMyCoupons">查看我的優惠券</button>
        </template>
      </div>
    </div>
  </div>
</template>

<script>
import {
  ALREADY_CLAIMED_IN_PERIOD,
  COUPON_BIRTHDAY_REQUIRED,
  BIRTHDAY_UNREADABLE_TEXT,
  COUPON_DATE_FORMAT,
  formatCouponDateTime,
  isRetryableCouponError,
} from "@/utils/coupon.utils";
import {
  getCouponPublicRuleDetail,
  getTemplateValidityText,
} from "@/utils/couponTemplate.utils";
import { getClaimOfferUuid, getBirthdayFormRoute } from "@/utils/couponClaimLink.utils";

export default {
  name: "CouponClaimPopup",
  props: { offerUuid: String },
  emits: ["close"],
  data() {
    return {
      isLoadingDetail: Boolean(this.offerUuid),
      hasLoadedDetail: false,
      hasAutoClaimed: false,
      hasClaimed: false,
      claimedCouponUuid: "",
      isClaimPending: false,
      claimErrorCode: "",
      claimErrorMessage: "",
    };
  },
  computed: {
    getStoreCode() {
      return this.$store.getters["userData/storeCode"];
    },
    getOfferDetailState() {
      const state = this.$store.getters["coupon/couponOfferDetail"];
      return state.uuid === this.offerUuid ? state : null;
    },
    getOfferDetail() {
      return this.getOfferDetailState?.data || null;
    },
    isLoading() {
      return this.isLoadingDetail || Boolean(this.getOfferDetailState?.loading) || this.isClaiming;
    },
    isClaiming() {
      return this.isClaimPending || this.$store.state.coupon.isClaiming;
    },
    getLoadError() {
      if (!this.offerUuid) return "此領券連結已失效或無法使用";
      return this.getOfferDetailState?.error || "此領券連結已失效或無法使用";
    },
    hasBirthday() {
      return Boolean(this.$store.state.userData?.memberInfo?.birthday);
    },
    getClaimErrorText() {
      return this.claimErrorCode === COUPON_BIRTHDAY_REQUIRED && this.hasBirthday
        ? BIRTHDAY_UNREADABLE_TEXT : this.claimErrorMessage;
    },
    getResultHint() {
      if (this.getResultStatus === "birthdayRequired") return "儲存生日後即可回來領取。";
      return this.getResultStatus === "claimed" ? "實際使用須符合優惠條件。" : "";
    },
    getResultStatus() {
      if (!this.hasLoadedDetail || !this.getOfferDetail) return "invalid";
      if (this.claimErrorCode === ALREADY_CLAIMED_IN_PERIOD) return "alreadyClaimed";
      if (this.claimErrorCode === COUPON_BIRTHDAY_REQUIRED && !this.hasBirthday) return "birthdayRequired";
      if (this.claimErrorMessage || !this.hasClaimed) return "failed";
      return "claimed";
    },
    getResultTitle() {
      if (this.isLoading) return "領取優惠券";
      return { birthdayRequired: "請先填寫生日", claimed: "領取成功", alreadyClaimed: "已領取優惠券", failed: "無法領取優惠券", invalid: "無法領取優惠券" }[this.getResultStatus];
    },
    hasResultDetail() {
      return ["claimed", "alreadyClaimed"].includes(this.getResultStatus);
    },
    canRetry() {
      return this.getResultStatus === "failed" && isRetryableCouponError(this.claimErrorCode);
    },
    getLineOfficialAddress() {
      return this.$store.getters["basic/lineOfficialAddress"];
    },
    getClaimPeriodText() {
      const zone = this.getOfferDetail?.timezone_name;
      const begin = formatCouponDateTime(
        this.getOfferDetail?.claim_begin_at,
        zone,
        COUPON_DATE_FORMAT
      );
      const end = formatCouponDateTime(
        this.getOfferDetail?.claim_end_at,
        zone,
        COUPON_DATE_FORMAT
      );
      return `${begin} ～ ${end}`;
    },
    getValidityText() {
      return getTemplateValidityText(this.getOfferDetail?.public_rules, this.getOfferDetail?.timezone_name);
    },
    getPublicRuleDetail() {
      return getCouponPublicRuleDetail({
        publicRules: this.getOfferDetail?.public_rules,
      });
    },
  },
  async mounted() {
    if (this.hasAutoClaimed) return;
    this.hasAutoClaimed = true;
    if (await this.load()) await this.claim();
  },
  methods: {
    async load() {
      if (!this.offerUuid) {
        this.isLoadingDetail = false;
        return false;
      }
      this.isLoadingDetail = true;
      this.hasLoadedDetail = false;
      try {
        const result = await this.$store.dispatch(
          "coupon/fetchCouponOfferDetail",
          this.offerUuid
        );
        this.hasLoadedDetail = Boolean(result && this.getOfferDetail);
        return this.hasLoadedDetail;
      } catch (error) {
        return false;
      } finally {
        this.isLoadingDetail = false;
      }
    },
    async claim() {
      if (!this.hasLoadedDetail || !this.getOfferDetail || this.isLoading || this.hasClaimed) return;
      this.isClaimPending = true;
      this.claimErrorCode = "";
      this.claimErrorMessage = "";
      try {
        const result = await this.$store.dispatch(
          "coupon/claimCouponOffer",
          this.offerUuid
        );
        if (!result) throw new Error("領取失敗，請稍後再試");
        this.hasClaimed = true;
        // 共用 UUID 格式檢查；缺值或格式不符時改導已領取列表
        this.claimedCouponUuid = getClaimOfferUuid(result.member_coupon_uuid);
      } catch (error) {
        this.claimErrorCode = error?.code || "";
        this.claimErrorMessage = error?.message || "領取失敗，請稍後再試";
      } finally {
        this.isClaimPending = false;
      }
    },
    close() {
      if (!this.isLoading) this.$emit("close");
    },
    toBirthdayForm() {
      this.$emit("close");
      this.$router.push(getBirthdayFormRoute(this.getStoreCode, { offerUuid: this.offerUuid }));
    },
    toLineOfficialAccount() {
      window.location.href = this.getLineOfficialAddress;
    },
    // 本次領到的券直接開詳情頁；已領過等拿不到子券編號的情況導已領取列表
    toMyCoupons() {
      this.$emit("close");
      if (this.getResultStatus === "claimed" && this.claimedCouponUuid) {
        this.$router.push({
          path: `/${this.getStoreCode}/coupon/use/${this.claimedCouponUuid}`,
        });
        return;
      }
      this.$router.push({
        path: `/${this.getStoreCode}/coupon`,
        query: { tab: "claimed" },
      });
    },
  },
};
</script>

<style lang="scss" scoped>
.claim_overlay {
  position: fixed;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
  background-color: rgba(0, 0, 0, 0.5);
  z-index: 9999;
}

.claim_popup {
  position: relative;
  width: min(100%, 360px);
  max-height: 86vh;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  background-color: #f6f6f6;
  border-radius: 12px;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.2);
}

.claim_header {
  padding: 18px 44px 4px;
  color: #fd5001;
  font-size: 20px;
  font-weight: 700;
  text-align: center;
}

.claim_close {
  position: absolute;
  top: 10px;
  right: 10px;
  width: 32px;
  height: 32px;
  border: 0;
  border-radius: 50%;
  background: transparent;
  color: #8b7563;
  font-size: 22px;
  line-height: 32px;
}

.claim_body {
  min-height: 0;
  overflow-y: auto;
  padding-bottom: 4px;
}

.coupon_hero {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  margin: 12px 14px 0;
  padding: 16px 15px;
  background-color: #fff4e7;
  border: 1px solid #f1d9bb;
  border-radius: 10px;
}

.coupon_badge {
  flex-shrink: 0;
  padding: 4px 10px;
  border-radius: 4px;
  background-color: #fff2e8;
  color: #9b4e1b;
  font-size: 15px;
  white-space: nowrap;
}

.coupon_title {
  flex: 1;
  min-width: 0;
  margin: 0;
  color: #292929;
  font-size: 17px;
  font-weight: 600;
  line-height: 1.4;
  overflow-wrap: anywhere;
}

.coupon_detail_card {
  margin: 12px 14px;
  padding: 16px;
  background-color: #fff;
  border-radius: 8px;
  color: #292929;
  font-size: 14px;
  line-height: 1.6;

  h4 {
    margin: 14px 0 4px;
    color: #8b7563;
    font-size: 12px;
    font-weight: 500;
  }

  h4:first-child {
    margin-top: 0;
  }

  p {
    margin: 4px 0;
    overflow-wrap: anywhere;
  }
}

.claim_message {
  margin: 12px 14px 4px;
  color: #d93025;
  font-size: 14px;
  text-align: center;
  overflow-wrap: anywhere;
}

.claim_invalid {
  margin: 28px 14px 12px;
}

.claim_loading {
  padding: 40px 0;
  color: #666;
  font-size: 14px;
  text-align: center;
}

.claim_spinner {
  width: 36px;
  height: 36px;
  margin: 0 auto 12px;
  border: 4px solid #e5e5e5;
  border-top-color: #fd5001;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

.claim_hint {
  margin: 0 14px;
  color: #8b7563;
  font-size: 12px;
  text-align: center;
}

.claim_footer {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  padding: 12px 14px 16px;

  button {
    flex: 1;
    min-width: 0;
    border: 0;
    padding: 12px;
    border-radius: 6px;
    font-family: inherit;
    font-size: 15px;
    font-weight: 600;
    line-height: 1.5;
  }
}

.claim_footer .button_retry {
  flex-basis: 100%;
  background-color: #fbcb02;
  color: #242424;
}

.button_line {
  background-color: #06c755;
  color: #fff;
}

.button_coupons {
  background-color: #fd5001;
  color: #fff;
}
</style>
